import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import styledWithTheme from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMemo } from 'react';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { usePropertyDefinitionsByObjectTypeId } from '../../v2/hooks/usePropertyDefinition';
import { URANUS_LAYER } from 'foundations-components/transitional/constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Container = styledWithTheme.div(({
  theme
}) => {
  return {
    height: '48px',
    borderRadius: theme.borderRadius['100'],
    border: `${theme.borderWidth['100']} solid ${theme.color['border-caution-default']}`,
    boxSizing: 'border-box',
    boxShadow: theme.boxShadow['100'],
    whiteSpace: 'nowrap',
    backgroundColor: theme.color['fill-caution-subtle'],
    padding: `0px ${theme.space['300']}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };
});
const UnsavedChangesAlert = ({
  requests
}) => {
  const {
    data: definitions
  } = usePropertyDefinitionsByObjectTypeId(requests.map(request => request.objectTypeId));

  // While unlikely, it is possible to have multiple unsaved changes for different object types,
  // or multiple unsaved changes for the same property. So, to support a nice UI in the tooltip,
  // we need to do some work to handle these cases.
  const uniqueUnsavedChanges = useMemo(() => {
    const seen = new Set();
    return requests.flatMap(request => request.type === 'PROPERTY' ? request.updates.properties.map(property => {
      var _definitions$request$;
      return {
        objectTypeId: request.objectTypeId,
        name: property.name,
        label: definitions === null || definitions === void 0 || (_definitions$request$ = definitions[request.objectTypeId]) === null || _definitions$request$ === void 0 || (_definitions$request$ = _definitions$request$[property.name]) === null || _definitions$request$ === void 0 ? void 0 : _definitions$request$.property.label
      };
    }) : []).filter(change => {
      const key = `${change.objectTypeId}-${change.name}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [requests, definitions]);
  if (requests.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(Container, {
    "data-test-id": "unsaved-changes-alert",
    children: /*#__PURE__*/_jsx(Tooltip, {
      use: "longform",
      headingText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.autosave.unsavedChangesTooltipHeading"
      }),
      title: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(View, {
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.autosave.unsavedChangesTooltipBody",
            options: {
              count: uniqueUnsavedChanges.length
            }
          })
        }), /*#__PURE__*/_jsx(List, {
          styled: true,
          children: uniqueUnsavedChanges.map(change => /*#__PURE__*/_jsx(ListItem, {
            children: change.label
          }, change.name))
        })]
      }),
      zIndex: Number(URANUS_LAYER),
      children: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(Text, {
          typography: "heading-50",
          textDecoration: "underline",
          color: "text-interactive-default",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.autosave.unsavedChanges",
            options: {
              count: uniqueUnsavedChanges.length
            }
          })
        })
      })
    })
  });
};
export default UnsavedChangesAlert;